import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "@phosphor-icons/react/dist/ssr";
import { useTranslation } from "react-i18next";
import { EarlyAccessForm } from "@/components/early-access-form";
import { EARLY_ACCESS_MODAL_EVENT } from "@/lib/early-access";
import { trackModalClose, trackModalOpen } from "@/analytics/ga4";

export function EarlyAccessModal() {
  const { t } = useTranslation("common");
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const open = (trigger: "hash" | "event") => {
    setIsOpen((current) => {
      if (!current) {
        trackModalOpen({ modal_id: "early_access", trigger });
      }
      return true;
    });
  };

  const close = (method: "button" | "escape" | "overlay") => {
    setIsOpen((current) => {
      if (current) {
        trackModalClose({ modal_id: "early_access", method });
      }
      return false;
    });
  };

  useEffect(() => {
    if (!isMounted) return;

    const openFromEvent = () => open("event");
    const openFromHash = () => {
      if (window.location.hash === "#early-access") {
        open("hash");
      }
    };

    window.addEventListener(EARLY_ACCESS_MODAL_EVENT, openFromEvent);
    window.addEventListener("hashchange", openFromHash);
    openFromHash();

    return () => {
      window.removeEventListener(EARLY_ACCESS_MODAL_EVENT, openFromEvent);
      window.removeEventListener("hashchange", openFromHash);
    };
  }, [isMounted]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close("escape");
      }
    };

    document.addEventListener("keydown", onKeyDown);
    window.setTimeout(() => closeButtonRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  if (!isMounted || !isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-gray-950/70 px-3 py-4 backdrop-blur-sm sm:px-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          close("overlay");
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("earlyAccess.modal.ariaLabel")}
        className="relative w-full max-w-6xl overflow-y-auto rounded-[1.75rem] bg-gray-50 p-3 shadow-2xl outline-none max-h-[calc(100vh-2rem)] sm:p-4"
      >
        <button
          type="button"
          ref={closeButtonRef}
          onClick={() => close("button")}
          aria-label={t("earlyAccess.modal.close")}
          className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:border-gray-300 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          <X size={18} weight="bold" />
        </button>
        <div className="pt-10 sm:pt-8">
          <EarlyAccessForm />
        </div>
      </div>
    </div>,
    document.body,
  );
}
